"use client";

import { useEffect, useState } from "react";
import {
  CalendarDays,
  Clock,
  ChevronLeft,
  Share2,
  BookmarkPlus,
  ArrowLeft,
  Tag,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { blogContent } from "../data/blog"; // Make sure this path is correct
import { Header } from "../components/Header"; // Make sure this path is correct
import Footer from "../components/Footer"; // Make sure this path is correct
import { Helmet } from "react-helmet";

// --- Simplified fetchBlogBySlug ---
const fetchBlogBySlug = async (slug) => {
  const post = blogContent.find((blog) => blog.slug === slug);

  if (post) {
    let processedContent = "";

    // Now we expect content to always be a string (Markdown)
    if (typeof post.content === "string") {
      processedContent = formatMarkdownAsHtml(post.content);
    } else {
      // Fallback if content is missing or somehow not a string
      console.warn(`Blog post with slug "${slug}" has missing or invalid content format.`);
      processedContent = `<p>${post.description || "Content not available."}</p>`;
    }

    return {
      ...post,
      processedContent, // Use the content processed by formatMarkdownAsHtml
    };
  }

  return null;
};

// --- formatMarkdownAsHtml function (Keep this exactly as you had it) ---
// Simple Markdown to HTML converter function with improved semantics for SEO
const formatMarkdownAsHtml = (markdown) => {
  if (!markdown) return "";

  let html = markdown;

 // Replace code blocks with syntax highlighting
html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, language, code) => {
  // Proper HTML entity escaping for code blocks
  const escapedCode = code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  return `<pre class="bg-gray-800 text-white p-4 rounded-lg overflow-auto my-4"><code class="language-${
    language || "plaintext"
  }">${escapedCode}</code></pre>`;
});


  // Replace inline code
  html = html.replace(
    /`([^`]+)`/g,
    '<code class="bg-gray-100 px-1 py-0.5 rounded text-pink-600">$1</code>'
  );

  // Replace headings with proper semantic structure and ID attributes for deep linking
  html = html
    .replace(/^# (.*$)/gm, (match, title) => {
      const id = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, "") // Allow hyphens in IDs
        .replace(/\s+/g, "-");
      return `<h1 id="${id}" class="text-4xl font-bold mt-8 mb-5">${title.trim()}</h1>`; // Added trim()
    })
    .replace(/^## (.*$)/gm, (match, title) => {
      const id = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      return `<h2 id="${id}" class="text-3xl font-bold mt-7 mb-4">${title.trim()}</h2>`; // Added trim()
    })
    .replace(/^### (.*$)/gm, (match, title) => {
      const id = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      return `<h3 id="${id}" class="text-2xl font-bold mt-6 mb-3">${title.trim()}</h3>`; // Added trim()
    })
    .replace(/^#### (.*$)/gm, (match, title) => {
      const id = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      return `<h4 id="${id}" class="text-xl font-bold mt-5 mb-2">${title.trim()}</h4>`; // Added trim()
    });

  // Replace bold and italic text
  html = html
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>");

  // Replace horizontal rules AFTER headings and other block elements are done
  html = html.replace(/^\s*---\s*$/gm, '<hr class="my-8 border-gray-300">'); // Increased margin

  // Replace links with proper rel attributes for SEO
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
    if (url.startsWith("http") && !url.includes(window.location.hostname)) { // More robust check for external links
      return `<a href="${url}" class="text-[#7200B8] hover:underline" rel="noopener noreferrer nofollow" target="_blank">${text}</a>`; // Added nofollow
    }
    return `<a href="${url}" class="text-[#7200B8] hover:underline">${text}</a>`;
  });

 // Replace unordered lists more robustly
 html = html.replace(/(?:^|\n)(\s*[-*+]\s+.*(?:\n\s*[-*+]\s+.*)*)/g, (match, listContent) => {
    const items = listContent.trim().split('\n');
    const listItems = items.map(item =>
        `<li class="mb-2">${item.replace(/^\s*[-*+]\s+/, '').trim()}</li>`
    ).join('\n');
    return `\n<ul class="list-disc pl-6 my-4">\n${listItems}\n</ul>\n`;
 });

 // Replace ordered lists more robustly
 html = html.replace(/(?:^|\n)(\s*\d+\.\s+.*(?:\n\s*\d+\.\s+.*)*)/g, (match, listContent) => {
    const items = listContent.trim().split('\n');
    const listItems = items.map(item =>
        `<li class="mb-2">${item.replace(/^\s*\d+\.\s+/, '').trim()}</li>`
    ).join('\n');
    return `\n<ol class="list-decimal pl-6 my-4">\n${listItems}\n</ol>\n`;
 });


 // Replace paragraphs (handle multiple blank lines and ensure it doesn't wrap lists/headings)
 // Split by double line breaks first, then process remaining single lines
 const blocks = html.split(/\n\s*\n/);
 html = blocks.map(block => {
     block = block.trim();
     if (!block) return '';
     // Avoid wrapping things that are already HTML elements or look like lists/headings processed above
     if (block.startsWith('<') || block.match(/^(\*|-|\+|#+|\d+\.)\s/)) {
         return block;
     }
     // Wrap remaining lines/blocks in paragraphs
     return `<p class="mb-4 text-gray-700 leading-relaxed">${block}</p>`; // Added line-height
 }).join('\n');


  // Clean up potential artifacts like empty paragraphs sometimes generated by regex replacements
  html = html.replace(/<p>\s*<\/p>/g, '');

  return html.trim(); // Trim final output
};


// --- Removed convertStructuredToHtml function as it's no longer needed ---

// --- getRelatedBlogs function (Keep as is) ---
const getRelatedBlogs = (currentSlug, currentTags = []) => {
  if (currentTags && currentTags.length > 0) {
    const blogsWithScores = blogContent
      .filter((blog) => blog.slug !== currentSlug)
      .map((blog) => {
        const matchingTags = blog.tags
          ? blog.tags.filter((tag) => currentTags.includes(tag)).length
          : 0;
        return { ...blog, relevanceScore: matchingTags };
      });
    return blogsWithScores
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, 2);
  }
  return blogContent.filter((blog) => blog.slug !== currentSlug).slice(0, 2);
};

// --- createExcerpt function (Keep as is) ---
const createExcerpt = (content, maxLength = 160) => {
  const plainText = content
    .replace(/<\/h[1-6]>/g, ". ")
    .replace(/<\/p>/g, " ")
    .replace(/<\/li>/g, ", ")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (plainText.length <= maxLength) return plainText;
  const lastSpace = plainText.lastIndexOf(" ", maxLength);
  const truncated = lastSpace > 0 ? plainText.substring(0, lastSpace) : plainText.substring(0, maxLength);
  return truncated.trim() + "...";
};

// --- generateTableOfContents function (Keep as is) ---
const generateTableOfContents = (html) => {
  const headingRegex = /<h([2-3])\s+id="([^"]+)"[^>]*>(.*?)<\/h\1>/g;
  const toc = [];
  let match;
  while ((match = headingRegex.exec(html)) !== null) {
    const level = parseInt(match[1], 10);
    const id = match[2];
    const title = match[3].replace(/<[^>]+>/g, "").trim(); // Added trim()
    if (id && title) { // Ensure id and title are valid
       toc.push({ level, id, title });
    }
  }
  if (toc.length < 3) return ""; // Don't show TOC for very short articles
  let tocHtml = '<div class="bg-gray-50 p-5 rounded-lg mb-8 border border-gray-200 shadow-sm">'; // Added border/shadow
  tocHtml += '<h4 class="text-lg font-bold mb-3 text-gray-800">Table of Contents</h4>'; // Darker heading
  tocHtml += '<nav class="toc">';
  tocHtml += '<ul class="space-y-2">';
  toc.forEach((item) => {
    const indentClass = item.level === 3 ? "ml-4" : "";
    tocHtml += `<li class="${indentClass}">`;
    // Smoother scroll behavior if supported
    tocHtml += `<a href="#${item.id}" onclick="document.getElementById('${item.id}').scrollIntoView({ behavior: 'smooth' }); return false;" class="text-[#7200B8] hover:underline flex items-center text-sm">`; // Smaller font
    tocHtml += `<span class="mr-2 text-purple-300">•</span>${item.title}</a></li>`;
  });
  tocHtml += "</ul>";
  tocHtml += "</nav>";
  tocHtml += "</div>";
  return tocHtml;
};


// --- BlogDetailPage Component (Largely the same, just uses the simplified fetch) ---
export default function BlogDetailPage() {
  const params = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableOfContents, setTableOfContents] = useState("");

  useEffect(() => {
    const fetchBlogPost = async () => {
      setLoading(true); // Set loading true at the start
      setError(null); // Reset error
      setTableOfContents(""); // Reset TOC

      try {
        const slug = params?.slug;
        if (!slug) {
          setError("Blog post slug is missing.");
          setLoading(false);
          return;
        }

        // Use the simplified fetch function
        const blogData = await fetchBlogBySlug(slug);

        if (!blogData) {
          setError("Blog post not found.");
          setLoading(false);
          return;
        }

        setBlog(blogData);

        // Generate table of contents only if content is substantial
        if (blogData.processedContent && blogData.processedContent.length > 1000) {
          const toc = generateTableOfContents(blogData.processedContent);
          setTableOfContents(toc);
        } else {
           setTableOfContents(""); // Explicitly clear if not long enough
        }


        const related = getRelatedBlogs(slug, blogData.tags);
        setRelatedBlogs(related);

        setLoading(false);

        // Analytics tracking (keep as is)
        if (typeof window !== "undefined" && window.gtag) {
          window.gtag("event", "page_view", {
            page_title: blogData.title,
            page_path: `/blog/${slug}`,
            page_location: window.location.href,
          });
        }
      } catch (error) {
        console.error("Error fetching blog post:", error);
        setError("Failed to load blog post. Please try again later.");
        setLoading(false);
      }
    };

    fetchBlogPost();

    window.scrollTo(0, 0);
  }, [params?.slug]); // Depend only on the slug itself

  // --- Loading State ---
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Blog Post | HotelHero.fun</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Header />
        <div className="flex flex-col justify-center items-center min-h-[calc(100vh-200px)] bg-gray-50"> {/* Adjusted height */}
          <div className="w-12 h-12 rounded-full border-4 border-[#7200B8] border-t-transparent animate-spin mb-4"></div>
          <p className="text-gray-600 font-medium">Loading blog post...</p>
        </div>
        <Footer />
      </>
    );
  }

    // --- Error State ---
    if (error || !blog) {
        return (
          <>
            <Helmet>
              <title>Error | HotelHero.fun Blog</title>
              <meta name="robots" content="noindex" />
            </Helmet>
            <Header />
            <div className="flex flex-col justify-center items-center min-h-[calc(100vh-200px)] bg-gray-50 text-center px-4">
              <h1 className="text-2xl font-bold text-red-600 mb-4">Oops! Something went wrong.</h1>
              <p className="text-gray-700 mb-6">{error || "Could not load the blog post."}</p>
              <a
                href="/blog"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#7200B8] hover:bg-[#5a0094] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7200B8]"
              >
                <ArrowLeft size={18} className="mr-2" />
                Back to Blog List
              </a>
            </div>
            <Footer />
          </>
        );
      }


  // --- Metadata Generation (Keep as is) ---
  const metaDescription = blog.description || createExcerpt(blog.processedContent);
  const metaKeywords = blog.tags ? blog.tags.join(", ") : "blog, HotelHero.fun";
  const metaTitle = `${blog.title} | HotelHero.fun Blog`;
  const canonicalUrl = `${window.location.origin}/blog/${blog.slug}`; // Use window.location.origin for base URL
  const isoPublishDate = blog.publishDate ? new Date(blog.publishDate).toISOString() : new Date().toISOString();
  const articleText = blog.processedContent?.replace(/<[^>]+>/g, "") || ""; // Added optional chaining and fallback

  // --- Render Logic ---
  return (
    // Keep the entire return statement with Helmet, Header, main content, Footer etc. exactly as you had it.
    // The key change was standardizing the data and simplifying fetchBlogBySlug.
    // The JSX structure below should remain the same.
    <>
      {/* Enhanced SEO Metadata */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={`${window.location.origin}${blog.image || '/logo.png'}`} /> {/* Ensure full URL */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="HotelHero.fun" />
        <meta property="article:published_time" content={isoPublishDate} />
        {blog.tags && blog.tags.map((tag, index) => ( <meta property="article:tag" content={tag} key={`og-tag-${index}`} /> ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${window.location.origin}${blog.image || '/logo.png'}`} /> {/* Ensure full URL */}
        <meta name="author" content={blog.author?.name || "HotelHero.fun Team"} />
        {blog.tags && (<meta name="news_keywords" content={blog.tags.join(", ")} /> )}

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
            headline: blog.title,
            description: metaDescription,
            image: [`${window.location.origin}${blog.image || '/logo.png'}`], // Ensure full URL
            datePublished: isoPublishDate,
            dateModified: isoPublishDate, // Ideally, add a modifiedDate field to your data
            author: { "@type": "Person", name: blog.author?.name || "HotelHero.fun Team" },
            publisher: {
              "@type": "Organization",
              name: "HotelHero.fun",
              logo: { "@type": "ImageObject", url: `${window.location.origin}/logo.png`, width: "192", height: "192" } // Ensure full URL
            },
            keywords: metaKeywords,
            articleBody: articleText.substring(0, 10000), // Increased length limit slightly
            wordCount: articleText.split(/\s+/).filter(Boolean).length // Filter empty strings
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${window.location.origin}/blog` },
              { "@type": "ListItem", position: 3, name: blog.title, item: canonicalUrl }
            ]
          })}
        </script>
      </Helmet>
      
      {/* Use a wrapper div for background and min-height */}
      <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-[100dvh] text-whiteflex flex-col">
         <Header /> {/* Header outside the main content area for better structure */}

         <main className="flex-grow pt-20 md:pt-24 bg-gray-50"> {/* Adjust top padding if header height changes */}
              {/* Hero section */}
              <div className="relative h-80 sm:h-96 md:h-[30rem] bg-gray-800 overflow-hidden">
                   {/* Background Image */}
                   <div
                       className="absolute inset-0 bg-center bg-cover"
                       style={{
                           backgroundImage: `url(${blog.image || '/logo.png'})`, // Use fallback logo if needed
                           filter: "brightness(0.6) blur(2px)", // Adjusted brightness and added blur
                       }}
                       aria-hidden="true"
                   />
                    {/* Gradient Overlay */}
                   <div
                       className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent"
                       aria-hidden="true"
                   ></div>
                   {/* Content */}
                   <div className="relative z-10 h-full flex flex-col justify-end container max-w-5xl mx-auto px-4 sm:px-6 pb-12">
                       <a
                           href="/blog"
                           className="inline-flex items-center text-white mb-4 hover:text-purple-200 transition-colors text-sm"
                       >
                           <ArrowLeft size={16} className="mr-1.5" aria-hidden="true" />
                           <span>Back to all posts</span>
                       </a>
                       <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-4xl leading-tight shadow-text"> {/* Added shadow */}
                           {blog.title}
                       </h1>
                       <div className="flex flex-wrap items-center text-white/80 gap-x-4 gap-y-2 text-sm">
                           <div className="flex items-center">
                               <div className="w-8 h-8 bg-purple-200 rounded-full flex items-center justify-center text-[#5a0094] mr-2 ring-2 ring-white/20"> {/* Enhanced avatar */}
                                   {blog.author?.avatar ? (
                                       <img
                                           src={blog.author.avatar}
                                           alt={`${blog.author.name}'s avatar`}
                                           className="w-full h-full rounded-full object-cover"
                                           width="32"
                                           height="32"
                                           loading="lazy" // Changed to lazy
                                       />
                                   ) : (
                                       <span aria-hidden="true" className="font-semibold">
                                           {blog.author?.name?.charAt(0) || "H"}
                                       </span>
                                   )}
                               </div>
                               <span>{blog.author?.name || "HotelHero.fun Team"}</span>
                           </div>
                           <div className="flex items-center">
                               <CalendarDays size={14} className="mr-1.5 opacity-80" aria-hidden="true" />
                               <time dateTime={isoPublishDate}>
                                   {new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} {/* Formatted Date */}
                               </time>
                           </div>
                             <div className="flex items-center">
                                <Clock size={14} className="mr-1.5 opacity-80" aria-hidden="true" />
                                <span>{blog.readingTime || "5 min read"}</span>
                             </div>
                       </div>
                   </div>
               </div>


               {/* Main content area */}
               <div className="container max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16">
                   {/* Tags - moved below title for better flow */}
                   {blog.tags && blog.tags.length > 0 && (
                       <div className="flex flex-wrap gap-2 mb-8" aria-label="Post tags">
                           {blog.tags.map((tag, index) => (
                               <a // Make tags links (adjust href as needed)
                                   href={`/blog/tag/${tag.toLowerCase().replace(/\s+/g, "-")}`} // Example link structure
                                   key={index}
                                   className="bg-purple-100 text-[#5a0094] text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-purple-200 transition-colors"
                               >
                                   #{tag}
                               </a>
                           ))}
                       </div>
                   )}


                    {/* Article actions & Description - combined for space */}
                   <div className="flex flex-col md:flex-row justify-between md:items-start gap-6 mb-10 pb-6 border-b border-gray-200">
                       <p className="text-lg text-gray-600 max-w-2xl md:mt-1"> {/* Adjusted margin */}
                           {blog.description}
                       </p>
                       <div className="flex gap-3 shrink-0 mt-4 md:mt-0"> {/* Added shrink */}
                            {/* Share Button */}
                            <button
                                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
                                aria-label="Share this article"
                                title="Share this article" // Added title
                                onClick={() => {
                                if (navigator.share) {
                                    navigator.share({ title: blog.title, text: metaDescription, url: window.location.href })
                                    .catch((err) => console.error("Error sharing:", err));
                                } else {
                                    navigator.clipboard.writeText(window.location.href)
                                    .then(() => alert('Link copied to clipboard!')) // Give feedback
                                    .catch(err => console.error('Failed to copy:', err));
                                }
                                }}
                            >
                                <Share2 size={20} />
                            </button>
                            {/* Save Button Placeholder */}
                           <button
                                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
                                aria-label="Save this article for later"
                                title="Save this article (Feature coming soon!)" // Added title
                           >
                               <BookmarkPlus size={20} />
                           </button>
                       </div>
                   </div>


                   {/* Table of Contents */}
                   {tableOfContents && (
                       <div dangerouslySetInnerHTML={{ __html: tableOfContents }} />
                   )}


                   {/* Article content */}
                   {/* Adjusted prose classes for better styling */}
                   <article
                       className="prose prose-lg lg:prose-xl max-w-none prose-neutral prose-a:text-[#7200B8] hover:prose-a:text-[#5a0094] prose-headings:font-bold prose-headings:text-gray-900 prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl prose-p:text-gray-700 prose-p:leading-relaxed prose-li:my-1 prose-blockquote:border-l-[#7200B8] prose-blockquote:pl-4 prose-blockquote:italic prose-pre:bg-gray-800 prose-pre:text-sm prose-pre:p-4 prose-pre:rounded-lg prose-img:rounded-lg prose-img:shadow-md" // Added image styles
                       itemScope
                       itemType="https://schema.org/Article"
                   >
                       <meta itemProp="headline" content={blog.title} />
                       <meta itemProp="description" content={metaDescription} />
                       <meta itemProp="image" content={`${window.location.origin}${blog.image || '/logo.png'}`} />

                        {/* Render the processed HTML content */}
                       <div
                           itemProp="articleBody"
                           dangerouslySetInnerHTML={{ __html: blog.processedContent || "" }} // Fallback to empty string
                       />
                   </article>


                   {/* Article footer */}
                   <div className="mt-12 pt-8 border-t border-gray-200">
                         {/* Footer Tags (optional redundancy or different styling) */}
                        {blog.tags && blog.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-8">
                                {blog.tags.map((tag, index) => (
                                <a
                                    href={`/blog/tag/${tag.toLowerCase().replace(/\s+/g, "-")}`}
                                    key={`footer-tag-${index}`}
                                    className="inline-flex items-center bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm px-4 py-2 rounded-full transition-colors"
                                    aria-label={`View all posts tagged with ${tag}`}
                                >
                                    <Tag size={14} className="mr-2" aria-hidden="true" />
                                    {tag}
                                </a>
                                ))}
                            </div>
                        )}

                       <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                           <a
                               href="/blog"
                               className="inline-flex items-center text-[#7200B8] font-medium hover:underline"
                           >
                               <ChevronLeft size={16} className="mr-1" aria-hidden="true" />
                               Back to all posts
                           </a>
                            {/* Footer Actions (Redundant but common pattern) */}
                           <div className="flex gap-3">
                               <button
                                   className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                                   aria-label="Share this article"
                                   title="Share this article"
                                   onClick={() => { /* ... same share logic ... */ }}
                               >
                                   <Share2 size={20} />
                               </button>
                               <button
                                   className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                                   aria-label="Save this article for later"
                                   title="Save this article (Feature coming soon!)"
                               >
                                   <BookmarkPlus size={20} />
                               </button>
                           </div>
                       </div>
                   </div>


                   {/* Author bio */}
                   <div className="mt-16 bg-white rounded-xl p-6 md:p-8 shadow-md border border-gray-100"> {/* Enhanced styling */}
                       <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
                           {/* Avatar */}
                           <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-[#7200B8] shrink-0 ring-4 ring-purple-50"> {/* Enhanced avatar */}
                               {blog.author?.avatar ? (
                                   <img
                                       src={blog.author.avatar}
                                       alt={`${blog.author.name}'s avatar`}
                                       className="w-full h-full rounded-full object-cover"
                                       width="64"
                                       height="64"
                                       loading="lazy"
                                   />
                               ) : (
                                   <span className="text-2xl font-bold" aria-hidden="true">
                                       {blog.author?.name?.charAt(0) || "H"}
                                   </span>
                               )}
                           </div>
                            {/* Bio Text */}
                           <div>
                               <p className="text-sm font-semibold text-gray-500 mb-1">Written By</p>
                               <h3 className="text-xl font-bold text-gray-900 mb-2">
                                   {blog.author?.name || "The HotelHero.fun Team"}
                               </h3>
                               <p className="text-gray-700 mb-3 text-base">
                                   {blog.author?.bio || `The HotelHero.fun team provides insights, strategies, and updates for the HotelHero hotel tycoon game.`} {/* Generic bio */}
                               </p>
                               {/* Social Links Placeholder */}
                               {/* {blog.author?.socialLinks && ( ... render social links ... )} */}
                           </div>
                       </div>
                   </div>
               </div>


               {/* Related blogs */}
               {relatedBlogs.length > 0 && (
                   <div className="bg-gray-100 py-16"> {/* Contrasting background */}
                       <div className="container max-w-7xl mx-auto px-4 sm:px-6">
                           <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center"> {/* Centered title */}
                               You Might Also Like
                           </h2>
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                               {relatedBlogs.map((relatedBlog) => (
                                   <a
                                       href={`/blog/${relatedBlog.slug}`}
                                       key={relatedBlog.slug}
                                       className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 block" // Added block and hover effect
                                   >
                                       <div className="h-52 sm:h-60 overflow-hidden relative"> {/* Adjusted height */}
                                           <div
                                               className="absolute inset-0 bg-center bg-cover transform scale-100 group-hover:scale-105 transition-transform duration-500 ease-in-out" // Smoother zoom
                                               style={{ backgroundImage: `url(${relatedBlog.coverImage || relatedBlog.image || '/logo.png'})` }}
                                               aria-hidden="true"
                                           ></div>
                                           <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" aria-hidden="true"></div> {/* Slightly darker gradient */}
                                       </div>
                                       <div className="p-6">
                                           <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#7200B8] transition-colors"> {/* Title hover effect */}
                                               {relatedBlog.title}
                                           </h3>
                                           <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed"> {/* Smaller text */}
                                               {relatedBlog.description}
                                           </p>
                                           <div className="flex items-center justify-between mt-4 text-xs text-gray-500"> {/* Adjusted margin */}
                                               <div className="flex items-center gap-3"> {/* Added gap */}
                                                    <span className="flex items-center"><CalendarDays size={14} className="mr-1" aria-hidden="true" />{new Date(relatedBlog.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric'})}</span>
                                                    <span className="flex items-center"><Clock size={14} className="mr-1" aria-hidden="true" />{relatedBlog.readingTime || "5 min read"}</span>
                                               </div>
                                               <span className="text-[#7200B8] text-sm font-medium flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"> {/* Read more appears on hover */}
                                                   Read more →
                                               </span>
                                           </div>
                                       </div>
                                   </a>
                               ))}
                           </div>
                       </div>
                   </div>
               )}


               {/* FAQs Section Placeholder (Keep structure if you plan to add FAQs) */}
                {/*
               {blog.faqs && blog.faqs.length > 0 && (
                   <div className="bg-white py-16">
                       <div className="container max-w-4xl mx-auto px-4 sm:px-6">
                           <h2 className="text-2xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
                           <div className="space-y-6" itemScope itemType="https://schema.org/FAQPage">
                               {blog.faqs.map((faq, index) => (
                                   <div key={index} className="bg-gray-50 rounded-lg p-6" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                                       <h3 className="text-xl font-bold text-gray-900 mb-3" itemProp="name">{faq.question}</h3>
                                       <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                           <div itemProp="text" className="text-gray-700">{faq.answer}</div>
                                       </div>
                                   </div>
                               ))}
                           </div>
                       </div>
                   </div>
               )}
               */}
           </main>


         <Footer /> {/* Footer outside main */}


         {/* Back to Top Button - Keep as is */}
         <div className="fixed bottom-6 right-6 z-50 opacity-0 focus-within:opacity-100 hover:opacity-100 transition-opacity duration-300 group/backtt"> {/* Group for hover effect */}
           <a
             href="#" // Use # for same page scroll
             onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} // Smooth scroll via JS
             className="bg-[#7200B8] text-white p-3 rounded-full shadow-lg hover:bg-[#5a0094] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7200B8] block group-hover/backtt:opacity-100 opacity-70" // Default opacity slightly visible on hover parent
             aria-label="Back to top"
             title="Back to top" // Added title
           >
             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
               <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
             </svg>
           </a>
         </div>
       </div>
    </>
  );
}